import type {IndexCopilotSpace} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useNavigate} from '@github-ui/react-router'
import {ActionList, ActionMenu} from '@primer/react'
import {useState} from 'react'
import {Icon} from '@github-ui/pacer/Icon'

import {useFetchCopilotSpaces, useCustomCopilotsEnabled} from '../hooks'
import {useRecentCopilotSpaces} from '../hooks/use-recent-copilot-spaces'

import {useSpaceSelection} from '../hooks/use-space-selection'
import {COPILOT_SPACES_NEW_PATH} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'

import {ToggleButton} from '@github-ui/pacer/ToggleButton'

export function SpacesSelect() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const {handleSpaceSelect: setPendingSpace, clearSpaceSelection, activeCopilotSpaceId, readOnly} = useSpaceSelection()

  const handleCreateSpace = () => {
    navigate(COPILOT_SPACES_NEW_PATH)
    sendEvent('dotcom_chat.activate', {
      target: 'SPACES_INPUT_CREATE_SPACE',
      mode: 'immersive',
    })
  }

  const handleSpaceSelect = (space: IndexCopilotSpace) => {
    const isCurrentlySelected =
      activeCopilotSpaceId && space.id === activeCopilotSpaceId.id && space.owner === activeCopilotSpaceId.owner

    if (isCurrentlySelected) {
      clearSpaceSelection()
    } else {
      setPendingSpace({id: space.id, owner: space.owner})
      sendEvent('dotcom_chat.activate', {
        target: 'SPACES_INPUT_SELECT_SPACE',
        space: `${space.owner}/${space.id}`,
      })
    }
    setOpen(false)
  }

  const {data: sortedSpaces = []} = useRecentCopilotSpaces()
  const {data: allSpaces = []} = useFetchCopilotSpaces(true)

  const copilotSpacesEnabled = useCustomCopilotsEnabled()

  // If readOnly (if a thread exists) hide the menu. We can't yet assign
  // a space to an existing thread.
  if (!copilotSpacesEnabled || readOnly) {
    return
  }

  // Find the selected space in allSpaces
  const selectedSpaceData = activeCopilotSpaceId
    ? allSpaces.find(space => space.id === activeCopilotSpaceId.id && space.owner === activeCopilotSpaceId.owner)
    : undefined

  // Use the space's name and blue color if found, otherwise use default
  const displayName = selectedSpaceData ? selectedSpaceData.name : 'Space'

  return (
    <ActionMenu open={open} onOpenChange={setOpen}>
      <ActionMenu.Anchor>
        <ToggleButton
          icon={<Icon icon="spaces" />}
          isSelected={!!selectedSpaceData}
          onClick={() => {}} // ActionMenu will handle toggling
          hasMenu
          isMenuOpen={open}
          selectedItemLabel={displayName}
        >
          {displayName}
        </ToggleButton>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay style={sortedSpaces.length > 0 ? {width: '260px'} : {}}>
        <ActionList selectionVariant="single" role="menu" aria-label="Select a space">
          {sortedSpaces.length > 0 ? (
            <>
              <ActionList.Group>
                <ActionList.GroupHeading>Recent</ActionList.GroupHeading>
                {sortedSpaces.map(space => {
                  const isSelected =
                    !!activeCopilotSpaceId &&
                    space.id === activeCopilotSpaceId.id &&
                    space.owner === activeCopilotSpaceId.owner
                  return (
                    <ActionList.Item
                      role="menuitemradio"
                      key={`${space.owner}, ${space.id}`}
                      onSelect={() => handleSpaceSelect(space)}
                      selected={isSelected}
                      aria-checked={isSelected}
                    >
                      <span>{space.name}</span>
                    </ActionList.Item>
                  )
                })}
              </ActionList.Group>
              <ActionList.Divider />
              <ActionList.Item onSelect={handleCreateSpace}>Create a space</ActionList.Item>
            </>
          ) : (
            <ActionList.Item onSelect={handleCreateSpace}>Create your first space</ActionList.Item>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

try{ SpacesSelect.displayName ||= 'SpacesSelect' } catch {}